import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Checkbox, Button, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Tooltip } from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { audienceCsvColumns } from '../../constants/audienceConstants';
import { grey } from '../../theme/palette';
import { customListStyle } from '../../styles/ImportLeads';

const TransferList = ({ originalProps, duplicateProps, allProps, convertToCSV, headers }) => {
    const [checked, setChecked] = useState(new Set());
    const { originals, setOriginals } = originalProps;
    const { duplicates, setDuplicates } = duplicateProps;
    const { all } = allProps;

    // Memoize ID sets for O(1) lookups
    const originalIds = useMemo(() => new Set(originals?.map((ele) => ele.id) || []), [originals]);
    const duplicateIds = useMemo(() => new Set(duplicates?.map((ele) => ele.id) || []), [duplicates]);

    // Use memoized intersection
    const leftChecked = useMemo(() => [...checked].filter(id => originalIds.has(id)), [checked, originalIds]);
    const rightChecked = useMemo(() => [...checked].filter(id => duplicateIds.has(id)), [checked, duplicateIds]);

    const handleToggle = (value) => () => {
        setChecked(prev => {
            const newChecked = new Set(prev);
            if (newChecked.has(value)) {
                newChecked.delete(value);
            } else {
                newChecked.add(value);
            }
            return newChecked;
        });
    };

    const handleAllRight = () => {
        setDuplicates(duplicates.concat(originals));
        setOriginals([]);
        convertToCSV([]);
    };

    const handleCheckedRight = () => {
        const leftCheckedSet = new Set(leftChecked);
        setDuplicates(duplicates.concat(all?.filter((ele) => leftCheckedSet.has(ele.id))));
        const filteredData = originals?.filter((ele) => !leftCheckedSet.has(ele.id));
        setOriginals(filteredData);
        convertToCSV(filteredData);
        setChecked(prev => {
            const newChecked = new Set(prev);
            leftChecked.forEach(id => newChecked.delete(id));
            return newChecked;
        });
    };

    const handleCheckedLeft = () => {
        const rightCheckedSet = new Set(rightChecked);
        const mergedData = originals.concat(all?.filter((ele) => rightCheckedSet.has(ele.id)));
        setOriginals(mergedData);
        convertToCSV(mergedData);
        setDuplicates(duplicates?.filter((ele) => !rightCheckedSet.has(ele.id)));
        setChecked(prev => {
            const newChecked = new Set(prev);
            rightChecked.forEach(id => newChecked.delete(id));
            return newChecked;
        });
    };

    const handleAllLeft = () => {
        const mergedData = originals.concat(duplicates);
        setOriginals(mergedData);
        convertToCSV(mergedData);
        setDuplicates([]);
    };

    const VirtuosoTableComponents = {
        Scroller: React.forwardRef((props, ref) => (
            <TableContainer component={Paper} {...props} ref={ref} sx={{ border: `1px solid ${grey[400]}` }} />
        )),
        Table: (props) => (
            <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
        ),
        TableHead,
        TableRow: ({ ...props }) => <TableRow {...props} />,
        TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    };

    const filteredData = [{
        width: 70,
        label: 'Checked',
        dataKey: 'checked',
    }, ...audienceCsvColumns.filter((ele) => headers?.[0]?.includes(ele.dataKey))];

    const fixedHeaderContent = () => (
        <TableRow>
            {filteredData.map((column) => (
                <TableCell
                    key={column.dataKey}
                    variant='head'
                    align={column.numeric || false ? 'right' : 'left'}
                    style={{ width: column.width }}
                    sx={{
                        backgroundColor: 'background.paper',
                        maxWidth: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {column.label}
                </TableCell>
            ))}
        </TableRow>
    );

    const rowContent = (_index, row) => (
        <>
            {filteredData.map((column) => {
                if (column.dataKey === 'checked') {
                    return (
                        <TableCell key={column.dataKey} >
                            <Checkbox
                                checked={checked.has(row.id)}
                                onChange={handleToggle(row.id)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{
                                    'aria-labelledby': column.dataKey,
                                }}
                            />
                        </TableCell>
                    );
                }
                return (
                    <Tooltip key={column.dataKey} title={row[column.dataKey] || ''} arrow>
                        <TableCell
                            align={column.numeric || false ? 'right' : 'left'}
                            sx={{
                                maxWidth: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {row[column.dataKey]}
                        </TableCell>
                    </Tooltip>
                );
            })}
        </>
    );

    const customList = (items, title) => (
        <>
            <Typography sx={customListStyle}>{title} {`(${items?.length || 0})`}</Typography>
            <Paper sx={{ height: 400, width: '100%' }}>
                <TableVirtuoso
                    data={items}
                    components={VirtuosoTableComponents}
                    fixedHeaderContent={fixedHeaderContent}
                    itemContent={rowContent}
                />
            </Paper>
        </>
    );

    return (
        <Grid container spacing={2} justifyContent='center' alignItems='center'>
            <Grid item sx={{ minWidth: 'calc(50% - 40px)' }}>{customList(originals, 'Original data')}</Grid>
            <Grid item>
                <Grid container direction='column' alignItems='center'>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleAllRight}
                        disabled={originals.length === 0}
                        aria-label='move all duplicates'
                    >
                        ≫
                    </Button>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label='move selected duplicates'
                    >
                        &gt;
                    </Button>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label='move selected originals'
                    >
                        &lt;
                    </Button>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleAllLeft}
                        disabled={duplicates.length === 0}
                        aria-label='move all originals'
                    >
                        ≪
                    </Button>
                </Grid>
            </Grid>
            <Grid item sx={{ minWidth: 'calc(50% - 40px)' }}>{customList(duplicates, 'Duplicate data')}</Grid>
        </Grid>
    );
};

export default TransferList;

TransferList.propTypes = {
    originalProps: PropTypes.object,
    duplicateProps: PropTypes.object,
    allProps: PropTypes.object,
    convertToCSV: PropTypes.func,
    headers: PropTypes.array,
};
